import { Box, Card, CardContent } from '@mui/material';
import { displayBoolean } from '../../../common/helpers/displayBoolean';
import { FnaBuyAndSellNeeds, FNADetails } from '../../../common/types/analyses';
import { formatAmount } from '../../../common/components/dataGrid/dataGridEditNumberCell';
import { JSX } from 'react';
import { PolicyCardSectionProps } from '../../../common/components/policyCard/policyCardSection';
import PolicyCard from '../../../common/components/policyCard';

interface TraditionalLayoutProps {
  fna: FNADetails;
  buyAndSellNeeds: FnaBuyAndSellNeeds | undefined;
}

/**
 * Display the FNA buy and sell needs results (share buy back layout)
 */
const ShareBuyBackLayout = ({ fna, buyAndSellNeeds }: TraditionalLayoutProps): JSX.Element => {
  const fnaRoleplayer = fna.roleplayers[0];

  return (
    <Card className="mt-12">
      <CardContent>
        <Box className="border-b">
          <PolicyCard
            sections={[
              {
                topBorder: false,
                caption: 'policy owner',
                title: fnaRoleplayer.roleplayerName,
                inlineItems: true,
                cols: 4,
                items: [
                  { name: 'Tax status', value: fnaRoleplayer.roleplayerTaxStatusNote },
                  { name: 'Registration number', value: fnaRoleplayer.roleplayerIDReg },
                  {
                    name: 'Business valuation',
                    value: formatAmount(fna.buyandsell?.valuationValue),
                  },
                  {
                    name: 'Valuation method',
                    value: `${fna.buyandsell?.valuationMethodNote} | ${fna.buyandsell?.valuationBasisNote}`,
                  },
                ],
              },
            ]}
          />
        </Box>

        <Box className="mt-8 grid lg:grid-cols-2 gap-12">
          {buyAndSellNeeds?.needs.map((need) => (
            <PolicyCard
              key={need.id}
              className="overflow-hidden"
              sections={[
                {
                  topBorder: false,
                  caption: 'Insured life',
                  title: need.representativeRoleplayerName || need.partyRoleplayerName,
                  inlineItems: true,
                  items: [
                    need.representativeRoleplayerName && {
                      name: 'Representative life for',
                      value: need.partyRoleplayerName,
                    },
                    {
                      name: 'Tax status',
                      value:
                        need.representativeRoleplayerTaxStatusNote ||
                        need.partyRoleplayerTaxStatusNote,
                    },
                    {
                      name: 'ID number',
                      value: need.representativeRoleplayerIDReg || need.partyRoleplayerIDReg,
                    },
                    { name: 'Marriage type', value: need.marriageTypeNote },
                  ].filter(Boolean) as PolicyCardSectionProps['items'],
                },
                {
                  caption: 'Needs',
                  inlineItems: true,
                  items: [
                    {
                      name: 'Business interest',
                      value: `${need.interest}%`,
                    },
                    {
                      name: 'Total loan accounts',
                      value: formatAmount(need.loanaccount),
                    },
                    {
                      name: 'Existing provision',
                      value: formatAmount(need.existingprovision),
                    },
                    need.estateDuty > 0 && {
                      name: 'Estate duty',
                      value: `${formatAmount(need.estateDuty)} (${need.estateDutyRate}%)`,
                    },
                    {
                      name: 'DWT',
                      value: `${displayBoolean(need.dividendWithholdingTax)} ${
                        need.dividendWithholdingTax && need.dividendWithholdingTaxAmount
                          ? `(${formatAmount(need.dividendWithholdingTaxAmount)})`
                          : ''
                      }`,
                    },
                    {
                      name: 'Disability benefit',
                      value: `${displayBoolean(need.disabilityBenefit)} ${
                        need.disabilityBenefit && need.disabilityBenefitAmount
                          ? `(${formatAmount(need.disabilityBenefitAmount)})`
                          : ''
                      }`,
                    },
                    {
                      name: 'Cover required',
                      value: formatAmount(need.coverRequired),
                    },
                  ].filter(Boolean) as PolicyCardSectionProps['items'],
                },
                {
                  caption: 'Death benefit',
                  inlineItems: true,
                  items: [
                    { value: formatAmount(need.deathBenefit), large: true },
                    {
                      name: 'Net existing provision',
                      value: formatAmount(need.netExistingProvision),
                      className: '-mt-5',
                    },
                  ],
                },
              ]}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ShareBuyBackLayout;
